import { CONTACT_TYPE } from '@/constants/contact';
import { LINK_TYPE } from '@/constants/link';
import store from '@/store';
import vCard from 'vcf';
import { DEFAULT_VCF_MIME_TYPE } from '@/constants/attachments';

/**
 * Contact Class.
 *
 * @typedef {Object} Contact
 * @typedef {Object} Message
 *
 * @module Contact
 **/
export default class Contact {

  /**
   * @constructor
   * @this {Contact}
   *
   * @param {Object} RawContact - Contact information.
   **/
  constructor({
    firstName, lastName, company, number, type, readOnly, enterpriseId,
    id, userId, linkId, linkType, source, updateMessages, avatar
  }) {
    // Avoid using id if possible, please use contactId instead.
    this.id = id;

    // Basic Contact info

    this.firstName = firstName || '';
    this.lastName = lastName || '';
    this.company = company || '';
    this.number = number;
    this.type = type || null;
    this.readOnly = readOnly || null;

    // Avatars
    this.userId = userId;
    this.avatar = avatar || null;

    // Extended Contact

    this.linkId = linkId;
    this.linkType = linkType;
    this.source = source;
    this.updateMessages = updateMessages;
    this.fullEnterpriseId = enterpriseId || null;
  }

  /**
   * Public getters
   */

  /**
   * Returns a unique identifier for a contact.
   *
   * ContactId was created due to the following scenarios:
   * - Not all sources include an id nor userId
   * - A contact number can be repeated in different sources or different linkTypes
   *
   * E.g. +19876543210.ACCOUNT.AZURE
   *      +19876543210.ACCOUNT.SMS
   *      +19876543210.USER.SMS
   *
   * @returns {String}
   */
  get contactId() {
    return [this.number, this.linkType, this.source].join('.');
  }

  /**
   * Determines whether a contact is from current user or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isCurrentAgentContact() {
    const currentUser = store.getters['user/currentUser'];
    return this.number === currentUser.number;
  }

  /**
   * Returns a combination of first and last names separated with a single space.
   *
   * @returns {String}
   */
  get fullName() {
    return `${this.firstName} ${this.lastName}`.trim();
  }

  /**
   * Returns fullName if contact has first or last name otherwise it will return company name.
   * If no names and no company name it will return the contact number.
   *
   * @returns {String}
   */
  get displayName() {
    if (this.isCurrentAgentContact) {
      return store.getters['user/getUserName'];
    }
    return this.fullName || this.company || this.number;
  }

  /**
   * Returns a Material Design Icon name based on CONTACT_TYPE
   *
   * @returns {String}
   */
  get displayIcon() {
    let icon = 'mdi-card-account-phone';

    if (this.isMobileNumber) icon = 'mdi-cellphone';
    else if (this.isWorkNumber) icon = 'mdi-briefcase';
    else if (this.isHomeNumber) icon = 'mdi-home';

    return icon;
  }

  /**
   * Returns full name initials.
   *
   * @returns {String}
   */
  get initials() {
    let result = '';

    if (this.firstName) result += this.firstName[0];
    if (this.lastName) result += this.lastName[0];
    else if (this.firstName.length > 1) result += this.firstName[1];
    else if (this.lastName.length > 1) result += this.lastName[1];

    return result.toUpperCase();
  }

  /**
   * Returns the enterpriseId extracted from the complete form stored as fullEnterpriseId.
   *
   * @returns {String}
   */
  get enterpriseId() {
    return (this.fullEnterpriseId || '').replace(/^\w+-/g, '');
  }

  /**
   * Returns a VCF Card based on contact info.
   *
   * @returns {Object}
   */
  get vcfCard() {
    const vcfCard = new vCard();
    const type = this.type?.toLowerCase() || CONTACT_TYPE.mobile;

    vcfCard.set('n', `${this.lastName};${this.firstName}`, null);
    vcfCard.set('fn', `${this.firstName} ${this.lastName}`, null);
    vcfCard.set('org', this.company, null);
    vcfCard.set(`tel;type=${type},voice`, this.number, null);

    const plainText = vcfCard.toString('3.0', null);
    const file = new Blob([plainText], { type: DEFAULT_VCF_MIME_TYPE });
    const name = `${this.displayName.toLowerCase().replaceAll(' ', '-')}.vcf`;

    return { name, file, type: file.type, size: file.size, plainText };
  }

  /**
   * Determines whether the contact number is mobile number or not
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isMobileNumber() {
    return this.type?.toUpperCase() === CONTACT_TYPE.mobile;
  }

  /**
   * Determines whether the contact number is a work number or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isWorkNumber() {
    return this.type?.toUpperCase() === CONTACT_TYPE.work;
  }

  /**
   * Determines whether the contact number is a home number or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isHomeNumber() {
    return this.type?.toUpperCase() === CONTACT_TYPE.home;
  }

  /**
   * Determines whether a contact is from Azure or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isAzureContact() {
    return this.source === 'AZURE';
  }

  /**
   * Determines whether a contact is managed by the enterprise or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isEnterpriseContact() {
    return this.linkType === LINK_TYPE.account;
  }

  /**
   * Determines whether a contact is owned by the current user or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isUserContact() {
    return this.linkType === LINK_TYPE.user;
  }

  /**
   * Returns a clean/lean version of contact information.
   *
   * @returns {Object}
   */
  get lean() {
    return {
      id: this.id,
      number: this.number,
      firstName: this.firstName,
      lastName: this.lastName,
      type: this.type,
      company: this.company,
      updateMessages: this.updateMessages
    }
  }

  /**
   * Determines whether a contact is registered as favorite for current user or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isFavoriteContact() {
    return store.getters['preferences/getFavoriteContacts'].includes(this.number);
  }

  /**
   * Public methods
   */

  /**
   * Determines whether one string may be found within comparable Contact fields,
   * returning true or false as appropriate.
   *
   * @param {String} searchString
   *
   * @returns {Boolean}
   */
  includes(searchString) {
    const comparableFields = [
      this.firstName,
      this.lastName,
      this.company,
      this.number
    ].join(' ').toUpperCase();

    return comparableFields.includes((searchString || '').toUpperCase());
  }

  /**
   * Compares two given Contact objects for order.
   * Returns a negative integer, zero, or a positive integer as ContactA object is less than,
   * equal to, or greater than ContactB object.
   *
   * @param {Contact} contactA
   * @param {Contact} contactB
   *
   * @returns {Number}
   */
  static compare(contactA, contactB) {
    const comparableFieldsA = [contactA.displayName.toUpperCase()].join('');
    const comparableFieldsB = [contactB.displayName.toUpperCase()].join('');

    if (comparableFieldsA < comparableFieldsB) return -1;
    if (comparableFieldsA > comparableFieldsB) return 1;
    return 0;
  }

  /**
   * Build a VCF card object from a list of contacts.
   *
   * @param {Array<Contact>} contacts
   *
   * @returns {Object}
   */
  static getVcfCardFromContacts(contacts) {
    const plainText = contacts.map(contact => contact.vcfCard.plainText).join('\n');
    const file = new Blob([plainText], { type: DEFAULT_VCF_MIME_TYPE });
    const name = contacts[0].displayName.toLowerCase().replaceAll(' ', '-');

    return { name, file, type: file.type, size: file.size, plainText, numberOfContacts: contacts.length};
  }

  /**
   * Build a a list of contacts from a VCF card.
   *
   * @param {Object} vcfCarFile
   *
   * @returns {Array<Contact>}
   */
  static getContactsFromVcfCard(vcfCarFile) {
    const vcfContactCards = vCard.parse(atob(vcfCarFile.content).trim());
    const contacts = [];

    vcfContactCards.forEach(card => {
      const contactName = card.get('n') && !card.get('n').isEmpty() ? card.get('n').valueOf() : ';';
      const firstName = contactName.split(';')[1];
      const lastName = contactName.split(';')[0];
      const number = card.get('tel')?.valueOf();
      const type = card.get('tel')?.type ? card.get('tel').type[0].toUpperCase().replaceAll('-', '') : '';
      const company = card.get('org')?.valueOf();

      contacts.push(
        new Contact({
          firstName,
          lastName,
          number,
          type,
          company
        })
      );
    });

    return contacts;
  }

  /**
   * Determines whether a rawContact includes all minimum necessary fields or not,
   * returning true or false as appropriate.
   *
   * @param {Object} rawContact
   *
   * @returns {Boolean}
   */
  static isValid(rawContact) {
    return rawContact.number && rawContact.linkType && rawContact.source;
  }
}
